// ============================================================================
// DEVICE IMAGES SERVICE - frontend/src/services/deviceImages.ts
// API calls for device image capture and management
// ============================================================================

import { api } from './api';

export interface ImageAnnotation {
  type: 'circle' | 'arrow' | 'text' | 'rectangle';
  x: number;
  y: number;
  width?: number;
  height?: number;
  endX?: number;
  endY?: number;
  text?: string;
  color: string;
}

export interface DeviceImageMetadata {
  sessionId: string;
  deviceId: string;
  imageId: string;
  timestamp: string;
  s3Key: string;
  extractedText: string;
  phase: string;
  captureSource: 'device' | 'manual' | 'ai-generated';
  captureLabel?: string;
  addedToContext?: boolean;
  addedToGraph?: boolean;
  taggedForExport?: boolean;
  extractionStatus?: 'pending' | 'complete' | 'failed';
  tags?: string[];
  annotations?: ImageAnnotation[];
  presignedUrl: string;
}

export interface DeviceImageUploadResponse {
  success: boolean;
  imageId: string;
  s3Key: string;
  timestamp: string;
  status: 'processing';
}

export async function uploadDeviceImage(
  sessionId: string,
  imageBase64: string,
  deviceId?: string,
  currentPhase?: string,
  captureLabel?: string
): Promise<DeviceImageUploadResponse> {
  const response = await api.post('/api/device-images/upload', {
    sessionId,
    imageBase64,
    deviceId,
    currentPhase,
    captureLabel
  });
  return response.data;
}

export async function getDeviceImages(
  sessionId: string
): Promise<DeviceImageMetadata[]> {
  const response = await api.get(`/api/device-images/${sessionId}`);
  return response.data.images || [];
}

export async function deleteDeviceImage(
  sessionId: string,
  imageId: string
): Promise<void> {
  await api.delete(`/api/device-images/${sessionId}/${imageId}`);
}

export async function requestDeviceCapture(
  deviceId: string,
  sessionId: string
): Promise<void> {
  await api.post('/api/devices-n8n/request-capture', { deviceId, sessionId });
}

export async function addImageToContext(
  sessionId: string,
  imageId: string
): Promise<void> {
  await api.post(`/api/device-images/${sessionId}/${imageId}/add-to-context`);
}

export async function addImageToGraph(
  sessionId: string,
  imageId: string
): Promise<void> {
  await api.post(`/api/device-images/${sessionId}/${imageId}/add-to-graph`);
}

export async function updateImageTags(
  sessionId: string,
  imageId: string,
  tags: string[]
): Promise<void> {
  await api.put(`/api/device-images/${sessionId}/${imageId}/tags`, { tags });
}

export async function updateImageAnnotations(
  sessionId: string,
  imageId: string,
  annotations: ImageAnnotation[]
): Promise<void> {
  await api.put(`/api/device-images/${sessionId}/${imageId}/annotations`, { annotations });
}

export async function setImageTaggedForExport(
  sessionId: string,
  imageId: string,
  taggedForExport: boolean
): Promise<void> {
  await api.put(`/api/device-images/${sessionId}/${imageId}/tagged-for-export`, { taggedForExport });
}

export async function askAvatarAboutImage(
  sessionId: string,
  imageId: string,
  question: string,
  targetAvatarRoles?: string[]
): Promise<{ requestId: string }> {
  const response = await api.post(`/api/device-images/${sessionId}/${imageId}/ask-avatar`, {
    question,
    targetAvatarRoles
  });
  return response.data;
}

export async function bulkImageAction(
  sessionId: string,
  imageIds: string[],
  action: 'delete' | 'add-to-context' | 'add-to-graph' | 'update-tags',
  tags?: string[]
): Promise<{ results: { imageId: string; success: boolean; error?: string }[] }> {
  const response = await api.post(`/api/device-images/${sessionId}/bulk`, {
    imageIds,
    action,
    tags
  });
  return response.data;
}

export async function toggleDeviceRecording(
  deviceId: string,
  sessionId: string,
  action: 'start-recording' | 'stop-recording'
): Promise<void> {
  await api.post('/api/devices-n8n/toggle-recording', { deviceId, sessionId, action });
}

export async function toggleVoiceAgent(
  deviceId: string,
  sessionId: string,
  action: 'start' | 'stop',
  avatarRole?: string
): Promise<void> {
  await api.post(`/api/sessions/${sessionId}/devices/${deviceId}/voice-agent`, {
    action,
    avatarRole,
  });
}

export async function setDeviceSleepState(
  deviceId: string,
  sessionId: string,
  state: 'sleep' | 'wake'
): Promise<void> {
  await api.post(`/api/sessions/${sessionId}/devices/${deviceId}/sleep`, { state });
}

export async function queueDeviceEmotion(
  deviceId: string,
  sessionId: string,
  emotion: string
): Promise<void> {
  await api.post(`/api/sessions/${sessionId}/devices/${deviceId}/emotion`, { emotion });
}

export async function toggleFollowMe(
  deviceId: string,
  sessionId: string,
  enabled: boolean
): Promise<void> {
  await api.post(`/api/sessions/${sessionId}/devices/${deviceId}/follow-me`, { enabled });
}

export async function configureDeviceCapture(
  deviceId: string,
  sessionId: string,
  captureInterval: number,
  enabled: boolean
): Promise<void> {
  await api.post('/api/devices-n8n/configure-capture', {
    deviceId,
    sessionId,
    captureInterval,
    enabled
  });
}
